import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import type { OrgConfigUpdate } from '@lms/shared-types'

import { updateOrgConfig } from '../../api/org-config'
import { VISUALLY_HIDDEN } from '../../lib/visually-hidden'
import { Button } from '../../components/primitives/Button'
import { Field } from '../../components/primitives/Field'
import {
  faviconFromImage,
  FAVICON_SPEC,
  LOGO_SPEC,
  prepareFaviconFromFile,
  prepareLogo,
} from '../../lib/branding-assets'
import { useOrgConfig } from '../../lib/org-config-context'

const inputStyle: React.CSSProperties = {
  width: '100%',
  height: 38,
  padding: '0 10px',
  border: '1px solid var(--border)',
  borderRadius: 8,
  background: 'var(--surface)',
  color: 'var(--text)',
  fontFamily: 'var(--font-sans)',
  fontSize: 14,
}

const TEXT_FIELDS: ReadonlyArray<{
  key: 'companyName' | 'companyShortName' | 'appName'
  label: string
}> = [
  { key: 'companyName', label: 'Company name' },
  { key: 'companyShortName', label: 'Short name (sidebar glyph)' },
  { key: 'appName', label: 'App name (tab title + topbar)' },
]

interface AssetRowProps {
  label: string
  help: string
  ariaFile: string
  accept: string
  value: string
  /** 'logo' → wide preview; 'favicon' → 48px square. */
  kind: 'logo' | 'favicon'
  /** Dark swatch behind the preview (for on-dark logos). */
  previewDark?: boolean
  onPick: (file: File) => void | Promise<void>
  onRemove: () => void
  /** Extra control rendered inline (e.g. "Use logo" for the favicon). */
  extra?: React.ReactNode
}

function AssetRow({
  label,
  help,
  ariaFile,
  accept,
  value,
  kind,
  previewDark,
  onPick,
  onRemove,
  extra,
}: AssetRowProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null)

  const previewBox: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: '0 0 auto',
    width: kind === 'favicon' ? 48 : 96,
    height: 48,
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: previewDark ? 'var(--ink, #202945)' : 'var(--surface-2, var(--surface))',
    overflow: 'hidden',
  }

  return (
    <Field label={label} helperText={help}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={previewBox}>
          {value ? (
            <img
              src={value}
              alt={`${label} preview`}
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <span style={{ fontSize: 11, color: 'var(--text-2)' }}>No image</span>
          )}
        </div>
        <input
          ref={inputRef}
          type="file"
          aria-label={ariaFile}
          accept={accept}
          onChange={(e) => {
            const file = e.target.files?.[0]
            e.target.value = '' // allow re-picking the same file
            if (file) void onPick(file)
          }}
          tabIndex={-1}
          style={VISUALLY_HIDDEN}
        />
        <Button variant="secondary" onClick={() => inputRef.current?.click()}>
          {value ? 'Replace' : 'Upload image'}
        </Button>
        {extra}
        {value ? (
          <Button variant="tertiary" onClick={onRemove}>
            Remove
          </Button>
        ) : null}
      </div>
    </Field>
  )
}

/** Company identity + uploaded logo/favicon images. Assets are processed on a
 *  canvas and stored as base64 data URLs (re-validated server-side). Favicons
 *  are cropped-to-square from any image, so a logo can be reused directly.
 *  Rendered as a section of the Branding & Theme page. */
export function AdminBrandingTab(): JSX.Element {
  const org = useOrgConfig()
  const queryClient = useQueryClient()

  const [form, setForm] = useState({
    companyName: org.companyName,
    companyShortName: org.companyShortName,
    appName: org.appName,
    logoLightUrl: org.logoLightUrl ?? '',
    logoDarkUrl: org.logoDarkUrl ?? '',
    faviconUrl: org.faviconUrl ?? '',
  })
  const [assetError, setAssetError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const mutation = useMutation({
    mutationFn: () => {
      const patch: OrgConfigUpdate = {
        companyName: form.companyName,
        companyShortName: form.companyShortName,
        appName: form.appName,
        logoLightUrl: form.logoLightUrl || null,
        logoDarkUrl: form.logoDarkUrl || null,
        faviconUrl: form.faviconUrl || null,
      }
      return updateOrgConfig(patch)
    },
    onSuccess: () => {
      setSaved(true)
      void queryClient.invalidateQueries({ queryKey: ['org-config', 'public'] })
    },
  })

  function setValue(key: keyof typeof form, value: string): void {
    setForm((f) => ({ ...f, [key]: value }))
    setSaved(false)
  }

  async function run(work: () => Promise<string>, key: keyof typeof form): Promise<void> {
    setAssetError(null)
    try {
      setValue(key, await work())
    } catch (err: unknown) {
      setAssetError(err instanceof Error ? err.message : 'Could not use that image.')
    }
  }

  const anyLogo = form.logoDarkUrl || form.logoLightUrl

  return (
    <div style={{ display: 'grid', gap: 16, maxWidth: 620 }}>
      {TEXT_FIELDS.map((f) => (
        <Field key={f.key} label={f.label} required>
          <input
            type="text"
            aria-label={f.label}
            value={form[f.key]}
            onChange={(e) => setValue(f.key, e.target.value)}
            style={inputStyle}
          />
        </Field>
      ))}

      <AssetRow
        label="Logo (on light surfaces)"
        help={LOGO_SPEC.help}
        ariaFile="Logo (on light surfaces) file"
        accept={LOGO_SPEC.accept}
        value={form.logoDarkUrl}
        kind="logo"
        onPick={(file) => run(() => prepareLogo(file), 'logoDarkUrl')}
        onRemove={() => setValue('logoDarkUrl', '')}
      />

      <AssetRow
        label="Logo (on dark surfaces)"
        help={LOGO_SPEC.help}
        ariaFile="Logo (on dark surfaces) file"
        accept={LOGO_SPEC.accept}
        value={form.logoLightUrl}
        kind="logo"
        previewDark
        onPick={(file) => run(() => prepareLogo(file), 'logoLightUrl')}
        onRemove={() => setValue('logoLightUrl', '')}
      />

      <AssetRow
        label="Favicon"
        help={FAVICON_SPEC.help}
        ariaFile="Favicon file"
        accept={FAVICON_SPEC.accept}
        value={form.faviconUrl}
        kind="favicon"
        onPick={(file) => run(() => prepareFaviconFromFile(file), 'faviconUrl')}
        onRemove={() => setValue('faviconUrl', '')}
        extra={
          anyLogo ? (
            <Button
              variant="soft"
              onClick={() => void run(() => faviconFromImage(anyLogo), 'faviconUrl')}
            >
              Use logo
            </Button>
          ) : null
        }
      />

      {assetError ? (
        <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>{assetError}</span>
      ) : null}

      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Button
          variant="primary"
          disabled={mutation.isPending || !form.companyName || !form.appName}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Saving…' : 'Save branding'}
        </Button>
        {saved ? <span style={{ color: 'var(--success-fg)', fontSize: 13 }}>Saved</span> : null}
        {mutation.isError ? (
          <span style={{ color: 'var(--danger-fg)', fontSize: 13 }}>
            {mutation.error instanceof Error ? mutation.error.message : 'Save failed'}
          </span>
        ) : null}
      </div>
    </div>
  )
}
